import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { Button, Card, Grid, Icon, Modal, Text } from "~/components-shared";
import { AuthoritiesEnum, CRUDActionEnum, EventSourceEnum, IntegrationTypeEnum, StatusStateEnum } from "~/types/enums";
import eventsActionCreators from "~/store/events/actions";
import eventSelectors from "~/store/events/selectors";
import lists from "~/lib/lists";
import DailyEventListCard from "~/components/DailyEventListCard";
import integrationSelectors from "~/store/integrations/selectors";
import CalendarIntegrationsModal from "~/components/CalendarIntegrationsModal";
import GoogleEventReadModal from "~/components/GoogleEventReadModal";
import useAbacExt from "~/hooks/abac";
import { combineGoogleCalendarEvents } from "~/lib/helpers";
import googleEventSelectors from "~/store/googleEvents/selectors";
import googleEventsActionCreators from "~/store/googleEvents/actions";
const Legend = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding-top: 1em;
  list-style: none;
`;
const StyledEventListCard = styled(DailyEventListCard)`
  height: 100%;
`;
const LegendItem = styled.li`
  color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.text.secondary;
}};
  margin: 0 1em;

  &:before {
    content: "";
    display: inline-block;
    width: 12px;
    height: 12px;
    background-color: ${_ref2 => {
  let {
    $color
  } = _ref2;
  return $color;
}};
    border-radius: 50%;
    margin-right: 0.5em;
  }
`;
const EventCalendarCard = () => {
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const [day, setDay] = useState(moment());
  const year = day.year();
  const month = day.month() + 1;
  const status = useSelector(eventSelectors.getStatusByMonth(year, month));
  const googleStatus = useSelector(googleEventSelectors.getStatusByMonth(year, month));
  const googleIntegration = useSelector(integrationSelectors.getMemberIntegrationByType(IntegrationTypeEnum.GOOGLE));
  const filter = useSelector(eventSelectors.getFilterByMonth(year, month));
  const events = useSelector(eventSelectors.getEventsByMonth(year, month));
  const googleEvents = useSelector(googleEventSelectors.getEventsByMonth(year, month));
  const [open, setOpen] = useState(false);
  const [openEvent, setOpenEvent] = useState(undefined);
  const localizer = momentLocalizer(moment);
  const dispatch = useDispatch();
  const {
    userHas,
    userHasOneOf
  } = useAbacExt();
  const monthlyEvents = combineGoogleCalendarEvents(events, googleEvents);
  const calendarId = googleIntegration?.settings?.calendar?.activeCalendar;
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(eventsActionCreators.fetchMonthStarted(year, month));
    }
  }, [dispatch, year, month, status]);
  useEffect(() => {
    if (googleIntegration && googleStatus === StatusStateEnum.IDLE) {
      dispatch(googleEventsActionCreators.fetchMonthStarted(year, month, calendarId));
    }
  }, [dispatch, year, month, googleIntegration, googleStatus, calendarId]);
  useEffect(() => {
    if (googleIntegration && googleStatus !== StatusStateEnum.IDLE) {
      dispatch(googleEventsActionCreators.fetchMonthStarted(year, month, calendarId));
    }
  }, [dispatch, calendarId]);
  const handleCreate = () => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.CREATE, undefined));
  };
  const handleClickEvent = _ref3 => {
    let {
      id
    } = _ref3;
    const currentEvent = monthlyEvents.find(event => id === event.id);
    if (currentEvent?.source === EventSourceEnum.GOOGLE) {
      setOpenEvent(currentEvent);
    } else {
      dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.READ, id));
    }
  };
  const handleFilter = newFilter => {
    dispatch(eventsActionCreators.filteredByMonth(year, month, newFilter));
  };
  const handlePrevMonth = () => {
    setDay(moment(day).subtract(1, "M"));
  };
  const handleNextMonth = () => {
    setDay(moment(day).add(1, "M"));
  };
  const dayEvents = [...monthlyEvents.filter(_ref4 => {
    let {
      dateFrom,
      dateTo
    } = _ref4;
    const from = moment(dateFrom).valueOf();
    const to = moment(dateTo).valueOf();
    const dayStart = day.startOf("day").valueOf();
    const dayEnd = day.endOf("day").valueOf();
    return from >= dayStart && from <= dayEnd || to >= dayStart && to <= dayEnd || dayStart >= from && dayStart <= to;
  })];
  return /*#__PURE__*/React.createElement(Card, {
    shadow: true,
    s: {
      p: 8,
      mb: 8
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 5,
    template: "minmax(500px, 700px) 320px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 4,
    gap: 2,
    template: userHasOneOf([AuthoritiesEnum.INTEGRATIONS_READ]) ? "1fr auto 2.3rem auto 2.3rem" : "1fr 2.3rem auto 2.3rem",
    align: "center",
    s: {
      px: 4,
      mb: 3
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h2",
    weight: "medium"
  }, moment(day, "M", true).format("MMMM YYYY")), userHasOneOf([AuthoritiesEnum.INTEGRATIONS_READ]) && /*#__PURE__*/React.createElement(Button, {
    color: googleIntegration ? "successTransparent" : "transparent",
    fontSize: 3,
    s: {
      px: 3,
      py: 2
    },
    onClick: () => setOpen(true),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: googleIntegration ? "tick" : "cog",
      s: {
        mr: 2
      }
    }),
    rounded: true
  }, googleIntegration ? t("EventCalendarCard.syncedCalendar") : t("EventCalendarCard.syncCalendar")), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("EventCalendarCard.prevMonth"),
    onClick: handlePrevMonth,
    color: "transparent",
    s: {
      p: 1
    },
    rounded: true
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    display: "block",
    rotate: -90,
    scale: 6
  })), /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 4,
      py: 2
    },
    fontSize: 3,
    onClick: () => setDay(moment()),
    rounded: true
  }, t("EventCalendarCard.today")), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("EventCalendarCard.nextMonth"),
    onClick: handleNextMonth,
    color: "transparent",
    s: {
      p: 1
    },
    rounded: true
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    display: "block",
    rotate: 90,
    scale: 6
  }))), /*#__PURE__*/React.createElement(Calendar, {
    defaultDate: day,
    date: day,
    events: monthlyEvents.map(event => ({
      id: event.id,
      title: event.name,
      allDay: false,
      start: moment(event.dateFrom),
      end: moment(event.dateTo),
      class: event.type
    })),
    onSelectEvent: handleClickEvent,
    views: ["month"],
    toolbar: false,
    localizer: localizer,
    style: {
      minHeight: "460px",
      height: "460px",
      margin: "0 -20px"
    },
    selectable: true,
    onDrillDown: d => setDay(moment(d)),
    eventPropGetter: event => {
      const currentEvent = monthlyEvents.find(_ref5 => {
        let {
          id
        } = _ref5;
        return id === event.id;
      });
      if (currentEvent && currentEvent.type) {
        return {
          style: {
            backgroundColor: lists.events.types.colors(theme)[currentEvent.type]
          }
        };
      }
      return {};
    }
  }), /*#__PURE__*/React.createElement(Legend, null, Object.keys(lists.events.types.names).map(type => /*#__PURE__*/React.createElement(LegendItem, {
    key: type,
    $color: lists.events.types.colors(theme)[type]
  }, lists.events.types.shortenNames[type])))), /*#__PURE__*/React.createElement(Grid.Cell, {
    style: {
      height: "100%"
    },
    align: "start"
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    align: "center",
    s: {
      mt: 1,
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h4"
  }, t("EventCalendarCard.dayEvents")), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, userHas(AuthoritiesEnum.EVENTS_WRITE) && /*#__PURE__*/React.createElement(Button, {
    color: "grey",
    s: {
      px: 4,
      py: 2
    },
    fontSize: 3,
    onClick: handleCreate,
    rounded: true
  }, t("EventCalendarCard.createEvent")))), /*#__PURE__*/React.createElement(StyledEventListCard, {
    day: day.toISOString(),
    loading: !dayEvents.length && (status === StatusStateEnum.LOADING || googleStatus === StatusStateEnum.LOADING),
    events: dayEvents,
    filter: filter,
    onFilter: handleFilter,
    maxHeight: "500px"
  }))), /*#__PURE__*/React.createElement(Modal, {
    open: open
  }, /*#__PURE__*/React.createElement(CalendarIntegrationsModal, {
    onClose: () => setOpen(false)
  })), /*#__PURE__*/React.createElement(Modal, {
    open: !!openEvent
  }, /*#__PURE__*/React.createElement(GoogleEventReadModal, {
    event: openEvent,
    onClose: () => setOpenEvent(undefined)
  })));
};
export default EventCalendarCard;